import React, { useCallback, useState } from 'react'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Button } from '@/atoms/Button'
import { ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { AngelDialog } from '@/molecules/AngelDialog'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface TellYourStoryContextValues {
  openModal: () => void
  closeModal: () => void
  setRelatedOrderId: (orderId: string) => void
  isSubmitted: boolean
}

export const TellYourStoryContext = React.createContext<TellYourStoryContextValues>({
  openModal: () => undefined,
  closeModal: () => undefined,
  setRelatedOrderId: () => undefined,
  isSubmitted: false,
})

interface TellYourStoryPromptProviderProps {
  children: React.ReactNode
  projectSlug: string
  projectName: string
  relatedOrderId?: string
}

export const PIF_ATTRIBUTION_THANK_YOU = gql`
  mutation thankYouMutation($input: ThankYouInput!) {
    thankYouMutation(input: $input) {
      thankYouNote
    }
  }
`

export const TellYourStoryPromptProvider: React.FC<TellYourStoryPromptProviderProps> = ({
  children,
  projectSlug,
  projectName,
}) => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const [relatedOrderId, setRelatedOrderId] = useState<string>()
  const [submitStory, { called }] = useMutation(PIF_ATTRIBUTION_THANK_YOU, {
    errorPolicy: 'all',
  })
  const track = useSafeTrack()

  const openModal = useCallback(() => {
    track('PIF Story Prompt Opened', { projectSlug })
    setOpen(true)
  }, [projectSlug, track, setOpen])

  const closeModal = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const onSubmit = useCallback(
    ({ thankYouNote }: onSubmitProps) => {
      track('PIF Story Submitted', { projectSlug })

      return submitStory({
        variables: {
          input: {
            relatedOrderId,
            thankYouNote,
            projectSlug,
          },
        },
      })
    },
    [submitStory, track, relatedOrderId, projectSlug],
  )

  const values = {
    openModal,
    closeModal,
    setRelatedOrderId,
    isSubmitted: called,
    onSubmit,
  }

  return (
    <TellYourStoryContext.Provider value={values}>
      {children}
      <TellYourStoryPrompt projectName={projectName} isOpen={isOpen} onClose={closeModal} onSubmit={onSubmit} />
    </TellYourStoryContext.Provider>
  )
}

export const useTellYourStoryPrompt = () => {
  return React.useContext(TellYourStoryContext)
}

interface onSubmitProps {
  thankYouNote: string
}

interface TellYourStoryPromptProps {
  projectName: string
  isOpen: boolean
  onClose: () => void
  onSubmit: (props: onSubmitProps) => void
}

export const TellYourStoryPrompt: React.FC<TellYourStoryPromptProps> = ({ isOpen, onClose, onSubmit, projectName }) => {
  const [thankYouNote, setThankYouNote] = useState<string>('')
  const [isAcceptedTerms, setAcceptedTerms] = useState<boolean>(false)

  const { t } = useTranslate('common')

  return (
    <AngelDialog
      isDarkMode={false}
      className="w-3/4 lg:w-1/2 lg:min-w-[300px] lg:max-w-[600px]"
      onClose={onClose}
      open={isOpen}
      title={t('shareYourStory', 'Share Your Story')}
    >
      <ParagraphMD>
        <Translate i18nKey="shareWhyYouHelpedSomeoneForShow" t={t} values={{ projectName }}>
          Share why you want to help someone else see {{ projectName }}.
        </Translate>
      </ParagraphMD>
      <textarea
        onChange={(e) => setThankYouNote(e.target.value)}
        placeholder="Tell us why..."
        className="mb-2 mt-4 min-h-[80px] w-full rounded-lg border border-core-gray-600"
      />
      <div className="mb-4 flex gap-2">
        <input type="checkbox" checked={isAcceptedTerms} onChange={() => setAcceptedTerms((previous) => !previous)} />
        <ParagraphSM className="text-xs" weight="normal">
          <Translate t={t} i18nKey="shareAcknowledgement">
            I acknowledge the{' '}
            <a className="font-semibold underline" href={paths.legal.termsOfUse}>
              Terms of Use
            </a>{' '}
            and that this post may be viewed publicly.
          </Translate>
        </ParagraphSM>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="gray" onClick={onClose}>
          <Translate t={t} i18nKey="notNow">
            Not Now
          </Translate>
        </Button>
        <Button
          disabled={!isAcceptedTerms || thankYouNote?.trim()?.length < 1}
          variant="oxide"
          onClick={() => {
            onSubmit({ thankYouNote })
            onClose()
          }}
        >
          <Translate t={t} i18nKey="shareYourStory">
            Share Your Story
          </Translate>
        </Button>
      </div>
    </AngelDialog>
  )
}
